import { MAX_QUESTION_PAPER_TOTAL_MARKS, MIN_QUESTION_PAPER_TOTAL_MARKS, totalMarksMinimumMessage } from "../data/question-paper/builder";
import {
  finalizeErrors,
  normalizeCatalogValue,
  validateOptionalText,
  validateRequiredText,
  validateRequiredPositiveNumber,
  validateStandardCurriculumBody,
  validateStringListLabels,
  validateCatalogLabel,
  MAX_LEARNING_OBJECTIVES_LENGTH,
  MAX_PASTED_CONTENT_LENGTH,
  MAX_REGENERATION_INSTRUCTIONS_LENGTH,
  MAX_TOPIC_LENGTH,
} from "./common";
import {
  CONCEPT_QUESTION_TONE_LABELS,
  DIFFICULTY_LABELS,
  KNOWLEDGE_DEPTH_LABELS,
  QUESTION_PAPER_TYPE_LABELS,
  QUESTION_TONE_LABELS,
  QUESTION_TYPE_LABELS,
  REAL_WORLD_DIFFICULTY_LABELS,
  SIMPLIFICATION_LEVEL_LABELS,
  getStandardCurriculumSets,
  TEACHING_TARGET_FORMAT_LABELS,
  UNDERSTANDING_LEVEL_LABELS,
  VARIATION_KNOWLEDGE_DEPTH_LABELS,
  WORKSHEET_FORMAT_LABELS,
} from "./catalogs";
import { validateDocumentContent } from "./documentContent";

function validateTopicAndFormat(body, errors = {}) {
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Topic is required.",
  });
  validateCatalogLabel(
    "targetFormat",
    body.targetFormat,
    TEACHING_TARGET_FORMAT_LABELS,
    errors,
  );
  return errors;
}

export function validateQuickSheetsRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Enter a topic or choose a familiar topic to continue.",
  });
  validateCatalogLabel("difficulty", body.difficulty, DIFFICULTY_LABELS, errors);
  validateStringListLabels(
    "questionTypes",
    body.questionTypes,
    QUESTION_TYPE_LABELS,
    errors,
  );
  if (body.worksheetFormat) {
    validateCatalogLabel(
      "worksheetFormat",
      body.worksheetFormat,
      WORKSHEET_FORMAT_LABELS,
      errors,
    );
  }
  validateOptionalText("learningObjectives", body.learningObjectives, errors, {
    maxLength: MAX_LEARNING_OBJECTIVES_LENGTH,
  });
  validateOptionalText("teacherInstructions", body.teacherInstructions, errors, {
    maxLength: MAX_REGENERATION_INSTRUCTIONS_LENGTH,
  });
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  return finalizeErrors(errors);
}

export function validateConceptQuestionsRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("concept", body.concept, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Concept is required.",
  });
  validateStringListLabels(
    "knowledgeDepth",
    body.knowledgeDepth,
    KNOWLEDGE_DEPTH_LABELS,
    errors,
  );
  validateCatalogLabel(
    "questionTone",
    body.questionTone,
    CONCEPT_QUESTION_TONE_LABELS,
    errors,
  );
  return finalizeErrors(errors);
}

export function validateQuestionVariationsRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("concept", body.concept, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Concept is required.",
  });
  validateStringListLabels(
    "knowledgeDepth",
    body.knowledgeDepth,
    VARIATION_KNOWLEDGE_DEPTH_LABELS,
    errors,
  );
  validateCatalogLabel(
    "questionTone",
    body.questionTone,
    QUESTION_TONE_LABELS,
    errors,
  );
  return finalizeErrors(errors);
}

export function validateQuickAssessmentRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateTopicAndFormat(body, errors);
  return finalizeErrors(errors);
}

export function validateTeachThisTopicRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Enter a topic or choose a familiar topic to continue.",
  });
  validateCatalogLabel(
    "targetFormat",
    body.targetFormat,
    TEACHING_TARGET_FORMAT_LABELS,
    errors,
  );
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  validateOptionalText("learningObjectives", body.learningObjectives, errors, {
    maxLength: MAX_LEARNING_OBJECTIVES_LENGTH,
  });
  return finalizeErrors(errors);
}

export function validateExplainDifferentlyRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("concept", body.concept, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Enter a concept or choose a familiar concept to continue.",
  });
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  validateCatalogLabel(
    "understandingLevel",
    body.understandingLevel,
    UNDERSTANDING_LEVEL_LABELS,
    errors,
  );
  return finalizeErrors(errors);
}

export function validateContentSimplifierRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Topic is required.",
  });
  validateCatalogLabel(
    "simplificationLevel",
    body.simplificationLevel,
    SIMPLIFICATION_LEVEL_LABELS,
    errors,
  );
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  return finalizeErrors(errors);
}

export function validateSectionBuilderRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Topic is required.",
  });
  validateOptionalText("lens", body.lens, errors, { maxLength: 200 });
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  return finalizeErrors(errors);
}

export function validateDifficultyBalancerRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Topic is required.",
  });
  validateCatalogLabel(
    "targetFormat",
    body.targetFormat,
    TEACHING_TARGET_FORMAT_LABELS,
    errors,
  );
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  return finalizeErrors(errors);
}

export function validateDoubtSolverRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("doubt", body.doubt, errors, {
    maxLength: MAX_PASTED_CONTENT_LENGTH,
    requiredMessage: "Enter a student doubt to continue.",
  });
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  return finalizeErrors(errors);
}

export function validateSlideDeckRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Topic is required.",
  });
  validateRequiredText("deckType", body.deckType, errors, { maxLength: 80 });
  validateRequiredText("duration", body.duration, errors, { maxLength: 40 });
  validateRequiredText("style", body.style, errors, { maxLength: 80 });
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  return finalizeErrors(errors);
}

export function validateQuestionPaperRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Enter subject or topic content to continue.",
  });
  validateStringListLabels(
    "questionTypes",
    body.questionTypes,
    QUESTION_PAPER_TYPE_LABELS,
    errors,
    { maxItems: 8 },
  );
  validateRequiredText("examMode", body.examMode, errors, { maxLength: 40 });
  validateRequiredText("examName", body.examName, errors, { maxLength: 120 });
  validateRequiredText("examType", body.examType, errors, { maxLength: 40 });
  validateRequiredText("difficultyDistribution", body.difficultyDistribution, errors, {
    maxLength: 40,
  });
  validateRequiredText("answerKeyMode", body.answerKeyMode, errors, {
    maxLength: 40,
  });
  validateRequiredPositiveNumber("totalMarks", body.totalMarks, errors, {
    requiredMessage: "Total marks cannot be empty.",
    minMessage: totalMarksMinimumMessage,
    maxMessage: `Total marks must be at most ${MAX_QUESTION_PAPER_TOTAL_MARKS}.`,
    min: MIN_QUESTION_PAPER_TOTAL_MARKS,
    max: MAX_QUESTION_PAPER_TOTAL_MARKS,
  });
  validateRequiredPositiveNumber("durationMinutes", body.durationMinutes, errors, {
    requiredMessage: "Duration cannot be empty.",
    minMessage: "Duration must be at least 15 minutes.",
    maxMessage: "Duration must be at most 300 minutes.",
    min: 15,
    max: 300,
  });
  validateDocumentContent("pastedContent", body.pastedContent, errors);
  return finalizeErrors(errors);
}

export function validateRealWorldTaskRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Enter a task topic to continue.",
  });
  const difficulty = normalizeCatalogValue(body.difficulty);
  if (!REAL_WORLD_DIFFICULTY_LABELS.has(difficulty)) {
    errors.difficulty = {
      field: "difficulty",
      code: "UNSUPPORTED_VALUE",
      message: "Selected difficulty is not supported.",
    };
  }
  return finalizeErrors(errors);
}

export function validateProjectIdeasRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Enter a project topic to continue.",
  });
  return finalizeErrors(errors);
}

export function validateProjectPlanRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Topic is required.",
  });
  validateRequiredText("learningObjective", body.learningObjective, errors, {
    maxLength: MAX_LEARNING_OBJECTIVES_LENGTH,
    requiredMessage: "Learning objective is required.",
  });
  validateOptionalText("optionalConstraints", body.optionalConstraints, errors, {
    maxLength: MAX_PASTED_CONTENT_LENGTH,
  });
  validateRequiredText("ideaTitle", body.ideaTitle, errors, { maxLength: 200 });
  validateRequiredText("ideaDescription", body.ideaDescription, errors, {
    maxLength: 2000,
  });
  validateRequiredText("projectType", body.projectType, errors, { maxLength: 80 });
  return finalizeErrors(errors);
}

export function validateActivityIdeasRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Enter an activity topic to continue.",
  });
  validateRequiredText("activityType", body.activityType, errors, { maxLength: 40 });
  validateRequiredText("duration", body.duration, errors, { maxLength: 40 });
  return finalizeErrors(errors);
}

export function validateActivityPlanRequest(body) {
  const errors = {};
  validateStandardCurriculumBody(body, getStandardCurriculumSets(), errors);
  validateRequiredText("topic", body.topic, errors, {
    maxLength: MAX_TOPIC_LENGTH,
    requiredMessage: "Topic is required.",
  });
  validateRequiredText("activityTitle", body.activityTitle, errors, { maxLength: 200 });
  validateRequiredText("durationLabel", body.durationLabel, errors, { maxLength: 40 });
  validateRequiredText("bestUsedWhen", body.bestUsedWhen, errors, { maxLength: 500 });
  validateRequiredText("description", body.description, errors, { maxLength: 2000 });
  return finalizeErrors(errors);
}

const VALIDATORS = {
  "quick-sheets": validateQuickSheetsRequest,
  "concept-questions": validateConceptQuestionsRequest,
  "question-variations": validateQuestionVariationsRequest,
  "quick-assessment": validateQuickAssessmentRequest,
  "teach-this-topic": validateTeachThisTopicRequest,
  "explain-differently": validateExplainDifferentlyRequest,
  "content-simplifier": validateContentSimplifierRequest,
  "section-builder": validateSectionBuilderRequest,
  "difficulty-balancer": validateDifficultyBalancerRequest,
  "doubt-solver": validateDoubtSolverRequest,
  "slide-deck-generator": validateSlideDeckRequest,
  "question-paper": validateQuestionPaperRequest,
  "real-world-task": validateRealWorldTaskRequest,
  "project-builder-ideas": validateProjectIdeasRequest,
  "project-builder-plan": validateProjectPlanRequest,
  "activity-generator-ideas": validateActivityIdeasRequest,
  "activity-generator-plan": validateActivityPlanRequest,
};

export function validateGenerationRequest(toolId, body) {
  const validator = VALIDATORS[toolId];
  if (!validator) {
    return { ok: true, errors: {} };
  }
  return validator(body ?? {});
}

export function mapTeachThisTopicFieldError(field) {
  if (field === "pastedContent" || field === "topic") {
    return "topicText";
  }
  return field;
}

export function mapQuickSheetsFieldError(field) {
  if (field === "pastedContent" || field === "topic") {
    return "topicText";
  }
  return field;
}

export function mapGenerationFieldError(toolId, field) {
  if (toolId === "teach-this-topic") {
    return mapTeachThisTopicFieldError(field);
  }
  if (toolId === "quick-sheets") {
    return mapQuickSheetsFieldError(field);
  }
  if (toolId === "explain-differently") {
    if (field === "concept" || field === "pastedContent") {
      return "topicText";
    }
  }
  if (toolId === "real-world-task" && field === "topic") {
    return "topicText";
  }
  if (toolId === "doubt-solver" && field === "doubt") {
    return "doubtText";
  }
  if (toolId === "project-builder-ideas" && field === "topic") {
    return "topicText";
  }
  if (toolId === "project-builder-plan" && field === "topic") {
    return "topicText";
  }
  if (toolId === "activity-generator-ideas" && field === "topic") {
    return "topicText";
  }
  if (toolId === "activity-generator-plan" && field === "topic") {
    return "topicText";
  }
  if (
    (toolId === "content-simplifier" ||
      toolId === "section-builder" ||
      toolId === "difficulty-balancer") &&
    (field === "pastedContent" || field === "topic")
  ) {
    return "contentText";
  }
  if (
    toolId === "slide-deck-generator" &&
    (field === "topic" || field === "pastedContent")
  ) {
    return "topicText";
  }
  if (
    toolId === "question-paper" &&
    (field === "topic" || field === "pastedContent")
  ) {
    return "topicContent";
  }
  return field;
}
